import { useEffect, useRef, useState } from 'react';
import styles from './index.module.sass';
import Timer from './timer';
import { PATH } from '../../config';
import classnames from 'classnames/bind';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import { SET_SCAN_COUNTDOWN, SET_SCAN_RESULT, useAppContext } from 'use-app';
import loveCodeData from 'json/love_code';
import { setScanPayViewedBoardList } from 'service/local-data-provider';
import repo from '../../service/repository';
import { generateBarCode, generateScanCode } from 'helpers/generate-scan-code';
import PowerBy from 'components/power-by';
import Spin from 'components/spin';
import Card from 'components/card';
import ScanPaymentDialog from './scan-payment-dialog';
import cardMasterCard from 'assets/card_mastercard.svg';
import cardJcb from 'assets/card_jcb.svg';
import cardVisa from 'assets/card_visa.svg';
import iconArrow from 'assets/icon_cad_arrow.svg';
import SuccessPayment from './success';
import { useInterval } from 'helpers/time-helper';
import amplitude from 'service/amplitude';
import LoadingDialog from 'components/loading-dialog';
import {
  getScanPayReceiptCodeHiddenState,
  getScanPayViewedBoardList,
  saveScanPayReceiptCodeHiddenState,
} from 'service/local-data-provider';
import { ReactComponent as IconArrow } from 'assets/icon_cad_arrow.svg';

const cx = classnames.bind(styles);

const Success = () => {
  const { state: appState } = useAppContext();
  const history = useHistory();

  useEffect(() => {
    amplitude.scanpay_success_pageview(appState.scanResult?.data.chainStoreName, appState.scanResult?.data.amount);
  }, []);

  return (
    <div className={styles.success} style={{ background: '#15CFBE' }}>
      <SuccessPayment
        amount={appState.scanResult?.data.amount}
        name={appState.scanResult?.data.chainStoreName}
        button={{
          text: '查看明細',
          onClick: () => {
            history.push({
              pathname: PATH.ORDER_DETAIL,
              search: `?url=${appState.scanResult?.data.invoice_order_url}`,
            });
          },
        }}
      />
      <PowerBy invert={true} />
    </div>
  );
};

const ScanPay = () => {
  const history = useHistory();
  const { state: appState, dispatch: appDispatch } = useAppContext();
  const creditCard = appState.me?.currentCreditCard;
  const timerRef = useRef();
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [expiredAt, setExpiredAt] = useState('');
  const [receiptBarcodeHide, setReceiptBarcodeHide] = useState(getScanPayReceiptCodeHiddenState() ?? false);
  const [scanPaymentDialog, setScanPaymentDialog] = useState(null);
  const [scanCountdownTime, setScanCountdownTime] = useState(0);
  const [tokenUrl, setTokenUrl] = useState(null);
  const [scanPayHint, setScanhint] = useState(null);

  useEffect(() => {
    document.body.style.touchAction = 'none';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.touchAction = 'inherit';
      document.body.style.overflow = 'inherit';
    };
  }, []);

  useEffect(() => {
    amplitude.scanpay_pageview();
  }, []);

  const resetCountdown = () => {
    appDispatch({ type: SET_SCAN_COUNTDOWN, payload: false });
    setScanCountdownTime(0);
  };

  useEffect(() => {
    if (appState.scanCountdown) {
      timerRef.current?.onPauseCountDown();
    }
  }, [appState.scanCountdown]);

  useInterval(
    () => {
      if (scanCountdownTime > 10) {
        resetCountdown();
        setScanPaymentDialog({
          type: 'payment_timeout',
        });
      } else {
        setScanCountdownTime(prev => prev + 1);
      }
    },
    appState.scanCountdown ? 1000 : null
  );

  useEffect(() => {
    if (appState.scanResult === null) return;

    if (appState.scanResult.error) {
      appDispatch({ type: SET_SCAN_COUNTDOWN, payload: false });
      setScanPaymentDialog({
        type: 'payment_fail',
        content: appState.scanResult.error.message,
      });
    }

    resetCountdown();
  }, [appState.scanResult]);

  const fetchBoards = () => {
    Promise.all([repo.fetchScanPayPopupBoard(), repo.fetchScanPayHintBoard()])
      .then(value => {
        const popupBoard = value[0][0];
        const hintBoard = value[1][0];

        if (!getScanPayViewedBoardList() || getScanPayViewedBoardList().indexOf(popupBoard.id) === -1) {
          setScanPayViewedBoardList(popupBoard.id);

          setScanPaymentDialog({
            type: 'popup_board',
            id: popupBoard.id,
            title: popupBoard.attributes.title,
            content: popupBoard.attributes.content,
            buttonText: popupBoard.attributes.buttonText,
          });
        }

        setScanhint({
          content: hintBoard.attributes.content,
          action: hintBoard.action,
        });
      })
      .catch(err => {
        console.log(err);
      });
  };

  const fetchScanPaymentData = async callback => {
    appDispatch({ type: SET_SCAN_RESULT, payload: null });
    setLoading(true);

    return await repo
      .fetchScanPaymentToken()
      .then(async data => {
        let scanCodeUrl = await generateScanCode(data.token);
        setToken(data.token.toUpperCase());
        setExpiredAt(data.expiredAt);
        setTokenUrl(() => ({ barcode: scanCodeUrl.barcode, qrcode: scanCodeUrl.qrcode }));
        if (callback) callback();
        setLoading(false);
      })
      .catch(error => {
        const content = () => {
          switch (error?.response?.data.error.code) {
            case 'user_has_no_credit_card':
              return '您沒有綁定付款方式，請綁定後再重新嘗試。';
            case 'user_payment_frozen':
              return '您的帳戶已被凍結，請解除凍結後再重新嘗試。';
            default:
              return '目前無法讀取付款條碼，請稍後再嘗試。';
          }
        };
        setScanPaymentDialog({ type: 'fetch_fail', content: content() });
      });
  };

  const receiptData = (() => {
    switch (appState.me?.receipt.type) {
      case 'carrier_member':
        return {
          type: 'carrier_member',
          name: '麻吉付會員載具',
          hint: '請記得現場與站員索取發票。',
        };
      case 'carrier_mobile_barcode':
        return {
          type: 'carrier_mobile_barcode',
          name: '手機條碼載具',
          value: appState.me.receipt.barcode,
        };
      case 'donate':
        return {
          type: 'donate',
          name: '捐贈愛心碼載具',
          value: appState.me.receipt.loveCode,
        };
      case 'b2b':
        return {
          type: 'b2b',
          name: '三聯式電子發票',
          hint: `請與站員告知開立統編「${appState.me.receipt.ubn}」`,
        };
      case 'carrier_citizen_digital':
        return {
          type: 'carrier_citizen_digital',
          name: '自然人憑證載具',
          value: appState.me.receipt.citizenDigitalNumber,
        };
      default:
        return {};
    }
  })();

  const donateData =
    receiptData?.type === 'donate' ? loveCodeData.find(data => data.love_code === receiptData?.value) : {};

  useEffect(() => {
    const init = async () => {
      await fetchScanPaymentData(() => {
        fetchBoards();
      });
    };

    init();
  }, []);

  const onRefresh = () => {
    amplitude.scanpay_refresh_onClick();
    timerRef.current.onRestartCountDown();
    fetchScanPaymentData();
  };

  const cardTypeImage = type => {
    switch (type) {
      case 'master':
        return cardMasterCard;
      case 'jcb':
        return cardJcb;
      default:
        return cardVisa;
    }
  };

  const SupportPois = () => {
    return (
      <div
        className={styles.modify}
        type="text"
        onClick={() => {
          amplitude.scanpay_support_onClick();
          history.push(`${PATH.MAPS}/gas-stations`);
        }}
        style={{ color: 'white' }}
      >
        支援場站
      </div>
    );
  };

  if (appState.scanResult !== null && appState.scanResult.status === 'success') return <Success />;

  const barcodeReceipt = ['carrier_citizen_digital', 'donate', 'carrier_mobile_barcode'];

  const ReceiptBarcodeCard = () => {
    if (barcodeReceipt.indexOf(receiptData.type) === -1) return <></>;

    let codeImageUrl;
    let codeText;

    if (receiptData.type === 'donate') {
      codeImageUrl = generateBarCode(donateData?.love_code);
      codeText = `${donateData.love_code} ${donateData.name}`;
    } else {
      codeImageUrl = generateBarCode(receiptData.value);
      codeText = receiptData.value;
    }

    const receiptCardClass = cx({
      'receipt-card': true,
      hide: receiptBarcodeHide,
    });

    return (
      <Card className={receiptCardClass}>
        <div className={styles.title}>
          <div
            className={styles.name}
            onClick={() => {
              amplitude.scanpay_receipt_toggle_onClick(!receiptBarcodeHide);
              setReceiptBarcodeHide(!receiptBarcodeHide);
              saveScanPayReceiptCodeHiddenState(!receiptBarcodeHide);
            }}
          >
            {receiptData.name}
            <div className={styles.arrow}>
              <img src={iconArrow} />
            </div>
          </div>
          <div
            className={styles.modify}
            onClick={() => {
              amplitude.scanpay_receipt_onClick();
              history.push(PATH.RECEIPT_INDEX);
            }}
          >
            更改
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.barcode}>
            <img src={codeImageUrl} alt="barcode" />
            <span>{codeText}</span>
          </div>
        </div>
      </Card>
    );
  };

  const ReceiptCard = () => {
    if (barcodeReceipt.indexOf(receiptData.type) !== -1) return <></>;

    return (
      <Card className={styles['receipt-card']}>
        <div className={styles.title}>
          {receiptData.name}
          <div
            className={styles.modify}
            type="text"
            onClick={() => {
              amplitude.scanpay_receipt_onClick();
              history.push(PATH.RECEIPT_INDEX);
            }}
          >
            更改
          </div>
        </div>
        <div className={styles.content}>{receiptData.hint}</div>
      </Card>
    );
  };

  const ScanCard = () => {
    if (loading) {
      return (
        <Card className={styles['loading-card']}>
          <Spin />
        </Card>
      );
    }

    return (
      <Card className={styles['scan-card']}>
        <div className={styles.card}>
          <div className={styles['card-logo']}>
            <img src={cardTypeImage(creditCard.cardType)} alt="arrow icon" />
          </div>
          <span className={styles['card-name']}>{creditCard.bank.shortName}</span>
          <span>····</span>&nbsp;
          {creditCard.lastFourCode}
        </div>
        <div className={styles.code}>
          <div className={styles.barcode}>
            <img src={tokenUrl.barcode} />
            <span>{token}</span>
          </div>
          <div className={styles.qrcode}>
            <img src={tokenUrl.qrcode} />
          </div>
          <div className={styles.countdown}>
            <Timer
              ref={timerRef}
              countDownSeconds={dayjs(expiredAt).diff(dayjs(), 's')}
              onRefresh={fetchScanPaymentData}
              onTimesUp={() => setScanPaymentDialog({ type: 'times_up', onClick: onRefresh })}
            />
          </div>
        </div>
      </Card>
    );
  };

  const getBoardDialogData = action => {
    if (action === null) return null;

    switch (action.type) {
      case 'action_dialog':
        return {
          image: action.actionDialog.image,
          title: action.actionDialog.title,
          content: action.actionDialog.message,
          buttons: [{ theme: 'positive', content: action.actionDialog.positiveButton }],
          action: () => {
            if (getBoardDialogData(action?.actionDialog.action)) {
              setScanPaymentDialog({
                type: 'hint_board',
                ...getBoardDialogData(action.actionDialog.action),
              });
            } else {
              setScanPaymentDialog(null);
            }
          },
        };
      case 'alert_dialog':
        return {
          image: action.alertDialog.image,
          title: action.alertDialog.title,
          content: action.alertDialog.message,
          buttons: [
            { theme: 'positive', content: action.alertDialog.positiveButton },
            { theme: 'cancel', content: action.alertDialog.negativeButton },
          ],
          action: () => {
            if (getBoardDialogData(action.alertDialog.action)) {
              setScanPaymentDialog({
                type: 'hint_board',
                ...getBoardDialogData(action.alertDialog.action),
              });
            } else {
              setScanPaymentDialog(null);
            }
          },
        };
      case 'message_box':
        return {
          title: action.messageBox.title,
          content: action.messageBox.message,
          buttons: [{ theme: 'positive', content: action.messageBox.positiveButton }],
          image: action.messageBox.image,
        };
      default:
        return null;
    }
  };

  const ScanPayHint = () => {
    if (!scanPayHint) return '';

    const clickableType = ['action_dialog', 'alert_dialog', 'message_box', 'open_link'];

    const Icon = () => {
      return clickableType.indexOf(scanPayHint.action.type) !== -1 ? <IconArrow /> : '';
    };

    const onClick = () => {
      const { action } = scanPayHint;

      switch (action.type) {
        case 'action_dialog':
        case 'alert_dialog':
        case 'message_box':
          setScanPaymentDialog({
            type: 'hint_board',
            ...getBoardDialogData(action),
          });
          break;
        case 'open_link':
          window.location.href = action.openLink.uri;
          break;
        default:
          return '';
      }
    };

    return (
      <div className={styles.hint} onClick={onClick}>
        {scanPayHint.content}
        <Icon />
      </div>
    );
  };

  return (
    <>
      <LoadingDialog isShow={appState.scanCountdown} />
      <ScanPaymentDialog data={scanPaymentDialog} onRefresh={onRefresh} setData={setScanPaymentDialog} />
      <div className={styles['scan-pay']}>
        <div className={styles.heading}>
          <h3>條碼付款</h3>
          <SupportPois />
        </div>
        <div className={styles.container}>
          {ReceiptBarcodeCard()}
          {ScanCard()}
          <ReceiptCard />
          <ScanPayHint />
        </div>
      </div>
    </>
  );
};

export default ScanPay;
